import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { getAmanQuote } from "../../../../api/amanServices";
import useAmanFlow from "../../../../hooks/useAmanFlow";
import useAmanFormData from "../../../../hooks/useAmanFormData";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import {
  selectAmanForm,
  selectAmanQuoteLoading,
  setAmanQuoteError,
  setAmanQuoteLoading,
  setAmanQuoteResponse,
} from "../../../../redux/features/amanFormSlice";
import {
  fetchCountriesList,
  selectCountries,
  selectCountriesLoader,
} from "../../../../redux/features/constants/countriesListSlice";
import {
  fetchNationalitiesList,
  selectNationalities,
  selectNationalitiesLoading,
} from "../../../../redux/features/constants/nationalitiesSlice";
import { selectLoginTokenInfo } from "../../../../redux/features/loginRegisterSlice";
import { selectProfileData } from "../../../../redux/features/profileSlice";
import {
  fetchPartyCode,
  selectPartyCode,
} from "../../../../redux/features/quoteSlice";
import {
  daysOptions,
  monthsOptions,
  yearsOptions,
} from "../../../../utils/calenderOptions";
import FileUpload from "../../../common/fileUploadInput/fileUploadInput";
import Input from "../../../common/Input/Input";
import SelectInput from "../../../common/selectInput/selectInput";
import FormTabs from "../../../common/tabs/formTabs";
import TitleDescription from "../../../newMotorPolicy/titleDescription/titleDescription";
import AmanJourneyLayout from "../../layouts/AmanJourneyLayout";
import BottomStickyBtnLayout from "../../layouts/BottomStickyBtnLayout";
import AmanSummaryModal from "../../modals/AmanSummaryModal";

const genderTabs = [
  { label_en: "Male", label_ar: "ذكر", type: "male" },
  { label_en: "Female", label_ar: "أنثى", type: "female" },
];

const occupationOptionsData = [
  { value: "maid", label_en: "Maid", label_ar: "عاملة منزلية", code: "maid" },
  { value: "driver", label_en: "Driver", label_ar: "سائق", code: "driver" },
  { value: "cook", label_en: "Cook", label_ar: "طباخ", code: "cook" },
  { value: "gardener", label_en: "Gardener", label_ar: "زراع", code: "gardener" },
  { value: "nanny", label_en: "Nanny", label_ar: "مربية", code: "nanny" },
  { value: "other", label_en: "Other", label_ar: "آخرى", code: "other" },
];

export const AMAN_DOMESTIC_HELPER_FORM = "AMAN_DOMESTIC_HELPER_FORM";

const DomesticHelperDetailsPage = ({ onClose }) => {
  const translate = useLanguageTranslate();
  const dispatch = useDispatch();
  const { handleNextSubstep, handlePrevSubstep } = useAmanFlow();

  const occupationOptions = occupationOptionsData.map((opt) => ({
    value: opt.value,
    label: translate(opt.label_en, opt.label_ar),
    code: opt.code,
  }));

  const nationalityOptions = useSelector(selectNationalities);
  const nationalitiesLoading = useSelector(selectNationalitiesLoading);
  const countriesData = useSelector(selectCountries);
  const countriesLoading = useSelector(selectCountriesLoader);
  const amanFormData = useSelector(selectAmanForm);
  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  const quoteLoading = useSelector(selectAmanQuoteLoading);
  const partyCode = useSelector(selectPartyCode);
  const profileData = useSelector(selectProfileData);
  const isLoggedIn = !!loginTokenInfo;
  // The profile API is the authoritative source for the logged-in user's
  // party code. Only fall back to the quote-slice partyCode when it's a
  // real code (never the "FALCON" guest fallback).
  const effectivePartyCode =
    profileData?.Code ||
    (partyCode && partyCode !== "FALCON" ? partyCode : null);

  const [formData, setFormData] = useState({
    fullName: "",
    passportNumber: "",
    cprNumber: "",
    domesticApplicationNumber: "",
    nationality: "",
    placeOfBirth: "",
    occupation: "",
    dateOfBirth: {
      day: "",
      month: "",
      year: "",
    },
    gender: "",
    passportUploaded: false,
    passportProcessing: false,
    passportData: null,
    passportProcessed: false,
  });

  const [errors, setErrors] = useState({});
  const [passportFile, setPassportFile] = useState(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);

  const { saveAmanDataInRedux, redux_form_data } = useAmanFormData(
    AMAN_DOMESTIC_HELPER_FORM,
    () => {},
  );

  useEffect(() => {
    if (nationalityOptions.length === 0 && !nationalitiesLoading) {
      dispatch(fetchNationalitiesList());
    }
  }, [dispatch, nationalityOptions.length, nationalitiesLoading]);

  useEffect(() => {
    if (!countriesData && !countriesLoading) {
      dispatch(fetchCountriesList());
    }
  }, [dispatch, countriesData, countriesLoading]);

  const countryOptions = countriesData
    ? countriesData.map((country) => ({
        value: country.Name || country.name,
        label: country.Name || country.name,
        label_ar: country.NameAr || country.nameAr || "",
        code: country.Code || country.code,
      }))
    : [];

  useEffect(() => {
    if (
      isLoggedIn &&
      !profileData?.Code &&
      !partyCode &&
      (loginTokenInfo?.userName || loginTokenInfo?.cpr)
    ) {
      dispatch(fetchPartyCode(loginTokenInfo?.userName || loginTokenInfo?.cpr));
    }
  }, [dispatch, isLoggedIn, profileData?.Code, partyCode, loginTokenInfo?.userName, loginTokenInfo?.cpr]);

  useEffect(() => {
    if (redux_form_data) {
      setFormData({
        fullName: redux_form_data.fullName || "",
        passportNumber: redux_form_data.passportNumber || "",
        cprNumber: redux_form_data.cprNumber || "",
        domesticApplicationNumber:
          redux_form_data.domesticApplicationNumber || "",
        nationality: redux_form_data.nationality || "",
        placeOfBirth: redux_form_data.placeOfBirth || "",
        occupation: redux_form_data.occupation || "",
        dateOfBirth: redux_form_data.dateOfBirth || {
          day: "",
          month: "",
          year: "",
        },
        gender: redux_form_data.gender || "",
        passportUploaded: redux_form_data.passportUploaded || false,
        passportProcessing: false,
        passportData: redux_form_data.passportData || null,
        passportProcessed: redux_form_data.passportProcessed || false,
        passportFile: redux_form_data.passportFile || null,
      });

      if (redux_form_data.passportFile) {
        setPassportFile(redux_form_data.passportFile);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (field, value) => {
    let processedValue = value;

    if (field === "fullName") {
      processedValue = value.replace(/[^a-zA-Z\s.]/g, "");
    } else if (field === "passportNumber") {
      processedValue = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    } else if (field === "cprNumber") {
      processedValue = value.replace(/\D/g, "");
      if (processedValue.length > 9) {
        processedValue = processedValue.substring(0, 9);
      }
    } else if (field === "domesticApplicationNumber") {
      processedValue = value.replace(/[^a-zA-Z0-9]/g, "");
    }

    setFormData((prev) => ({
      ...prev,
      [field]: processedValue,
    }));

    if (field === "nationality" && processedValue) {
      const isBahraini =
        processedValue === 14 ||
        (typeof processedValue === "string" &&
          (processedValue.toLowerCase().includes("bahrain") ||
            processedValue.toLowerCase().includes("بحرين")));

      if (isBahraini) {
        setErrors((prev) => ({
          ...prev,
          nationality: translate(
            "Helper nationality cannot be Bahraini",
            ".جنسية العامل المنزلي لا يمكن أن تكون بحرينية",
          ),
        }));
      } else {
        if (errors.nationality) {
          setErrors((prev) => ({ ...prev, nationality: "" }));
        }
      }
    } else {
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    }
  };

  const handleDateChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      dateOfBirth: {
        ...prev.dateOfBirth,
        [field]: value,
      },
    }));

    if (errors.dateOfBirth) {
      setErrors((prev) => ({ ...prev, dateOfBirth: "" }));
    }
  };

  const handleGenderChange = (gender) => {
    setFormData((prev) => ({
      ...prev,
      gender,
    }));

    if (errors.gender) {
      setErrors((prev) => ({ ...prev, gender: "" }));
    }
  };

  const handlePassportUpload = async (
    uploaded,
    fileName,
    passportData = null,
    fileSize = null,
  ) => {
    if (uploaded) {
      const fileInfo = {
        fileName: fileName,
        fileSize: fileSize || "Unknown",
        file: { name: fileName },
        uploadedAt: new Date().toISOString(),
      };

      setPassportFile(fileInfo);

      setFormData((prev) => ({
        ...prev,
        passportUploaded: true,
        passportProcessing: false,
        passportData: passportData,
        passportProcessed: !!passportData,
        passportFile: fileInfo,
      }));

      setErrors((prev) => ({ ...prev, passport: "" }));

      if (passportData) {
        setFormData((prev) => ({
          ...prev,
          passportNumber: passportData.passportNumber || prev.passportNumber,
          fullName: (passportData.fullName || prev.fullName).replace(/[^a-zA-Z\s.]/g, ""),
          dateOfBirth: passportData.dateOfBirth
            ? { ...passportData.dateOfBirth }
            : prev.dateOfBirth,
          gender: passportData.gender || prev.gender,
          nationality: passportData.nationality || prev.nationality,
        }));

        toast.success(
          translate(
            "Passport processed successfully! Form data has been auto-filled.",
            "تم معالجة جواز السفر بنجاح! تم ملء بيانات النموذج تلقائياً.",
          ),
        );
      } else {
        toast.info(
          translate(
            "Passport uploaded. Please fill the form manually.",
            "تم تحميل جواز السفر. يرجى ملء النموذج يدوياً.",
          ),
        );
      }
    } else {
      setPassportFile(null);
      setFormData((prev) => ({
        ...prev,
        passportUploaded: false,
        passportProcessing: false,
        passportData: null,
        passportProcessed: false,
        passportFile: null,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.passportUploaded) {
      newErrors.passport = translate(
        "Passport upload is required.",
        "تحميل جواز السفر مطلوب.",
      );
    }

    if (!formData.passportNumber.trim()) {
      newErrors.passportNumber = translate(
        "This field is required.",
        "هذا الحقل مطلوب.",
      );
    } else if (formData.passportNumber.length < 6) {
      newErrors.passportNumber = translate(
        "Passport number must be at least 6 characters.",
        "يجب أن يكون رقم جواز السفر 6 أحرف على الأقل.",
      );
    }

    if (!formData.cprNumber.trim()) {
      newErrors.cprNumber = translate(
        "This field is required.",
        "هذا الحقل مطلوب.",
      );
    } else if (!/^\d{9}$/.test(formData.cprNumber)) {
      newErrors.cprNumber = translate(
        "CPR must be 9 digits.",
        "يجب أن يتكون رقم الهوية الوطنية من 9 أرقام.",
      );
    }

    if (!formData.fullName.trim()) {
      newErrors.fullName = translate(
        "This field is required.",
        "هذا الحقل مطلوب.",
      );
    } else if (formData.fullName.trim().split(/\s+/).filter(name => name.length > 0).length < 2) {
      newErrors.fullName = translate(
        "Please enter first and last name.",
        "يرجى إدخال الاسم الأول واسم العائلة.",
      );
    }

    if (!formData.nationality) {
      newErrors.nationality = translate(
        "This field is required.",
        "هذا الحقل مطلوب.",
      );
    } else {
      const isBahraini =
        formData.nationality === 14 ||
        (typeof formData.nationality === "string" &&
          (formData.nationality.toLowerCase().includes("bahrain") ||
            formData.nationality.toLowerCase().includes("بحرين")));

      if (isBahraini) {
        newErrors.nationality = translate(
          "Helper nationality cannot be Bahraini",
          ".جنسية العامل المنزلي لا يمكن أن تكون بحرينية",
        );
      }
    }

    if (!formData.placeOfBirth) {
      newErrors.placeOfBirth = translate(
        "This field is required.",
        "هذا الحقل مطلوب.",
      );
    }

    if (
      !formData.dateOfBirth.day ||
      !formData.dateOfBirth.month ||
      !formData.dateOfBirth.year
    ) {
      newErrors.dateOfBirth = translate(
        "This field is required.",
        "هذا الحقل مطلوب.",
      );
    } else {
      const day = parseInt(formData.dateOfBirth.day);
      const month = parseInt(formData.dateOfBirth.month);
      const year = parseInt(formData.dateOfBirth.year);

      const birthDate = new Date(year, month - 1, day);
      const today = new Date();

      if (birthDate > today) {
        newErrors.dateOfBirth = translate(
          "Date of birth cannot be in the future.",
          "لا يمكن أن يكون تاريخ الميلاد في المستقبل.",
        );
      } else {
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (
          monthDiff < 0 ||
          (monthDiff === 0 && today.getDate() < birthDate.getDate())
        ) {
          age--;
        }

        if (age < 18) {
          newErrors.dateOfBirth = translate(
            "Helper must be at least 18 years old.",
            ".يجب أن يكون عمر العامل المنزلي 18 سنة على الأقل",
          );
        } else if (age > 59) {
          newErrors.dateOfBirth = translate(
            "This service is available for helpers aged 59 or below.",
            "هذه الخدمة متاحة للعاملين المنزليين بعمر 59 سنة أو أقل.",
          );
        }
      }
    }

    if (!formData.gender) {
      newErrors.gender = translate(
        "This field is required.",
        "هذا الحقل مطلوب.",
      );
    }

    if (!formData.occupation) {
      newErrors.occupation = translate(
        "This field is required.",
        "هذا الحقل مطلوب.",
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = async () => {
    if (!validateForm()) {
      return;
    }

    await saveAmanDataInRedux(formData);

    const planData = amanFormData?.AMAN_PLAN_SELECTION;
    const basicInfoData = amanFormData?.AMAN_BASIC_INFO;

    if (!isLoggedIn) {
      if (
        !basicInfoData?.cpr ||
        !basicInfoData?.fullName ||
        !basicInfoData?.contactNumber ||
        !basicInfoData?.email
      ) {
        toast.error(
          translate(
            "Please go back to start and complete your personal information.",
            "يرجى العودة إلى البداية وإكمال معلوماتك الشخصية.",
          ),
        );
        return;
      }
    }

    if (!planData?.planId) {
      toast.error(
        translate(
          "Please select a plan before continuing.",
          "يرجى اختيار خطة قبل المتابعة.",
        ),
      );
      return;
    }

    dispatch(setAmanQuoteLoading(true));

    try {
      const plan = planData?.planId === "aman_plus" ? "AMANPLUS" : "AMAN";

      const nameParts = formData.fullName.trim().split(" ");
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";

      const nationalityOption = nationalityOptions.find(
        (n) =>
          n.value === formData.nationality || n.code === formData.nationality,
      );
      const nationalityCode =
        nationalityOption?.code || formData.nationality || "";

      const memberDateOfBirth = `${formData.dateOfBirth.day?.padStart(2, "0")}/${formData.dateOfBirth.month?.padStart(2, "0")}/${formData.dateOfBirth.year}`;

      const genderCode =
        formData.gender === "male"
          ? "M"
          : formData.gender === "female"
            ? "F"
            : "";

      const quoteData = {
        isLoggedIn,
        plan,
        member: {
          cpr: formData.cprNumber,
          firstName,
          lastName,
          dateOfBirth: memberDateOfBirth,
          gender: genderCode,
          nationality: nationalityCode,
          countryOfResidence: "BH",
          maritalStatus: "SINGLE",
          passportNo: formData.passportNumber,
        },
      };

      let resolvedPartyCode = effectivePartyCode;
      if (isLoggedIn && !resolvedPartyCode && (loginTokenInfo?.userName || loginTokenInfo?.cpr)) {
        // Last-chance fetch so we don't submit without the user's real partyCode.
        try {
          const fetched = await dispatch(
            fetchPartyCode(loginTokenInfo?.userName || loginTokenInfo?.cpr)
          ).unwrap();
          const code = fetched?.data?.PartyCode || fetched?.PartyCode;
          if (code && code !== "FALCON") {
            resolvedPartyCode = code;
          }
        } catch (e) {
          // Swallow; proceed without partyCode rather than send FALCON.
        }
      }

      if (isLoggedIn && resolvedPartyCode) {
        quoteData.partyCode = resolvedPartyCode;
      } else if (!isLoggedIn) {
        quoteData.cpr = basicInfoData?.cpr || "";
        quoteData.fullName = basicInfoData?.fullName || "";
        quoteData.dateOfBirth = basicInfoData?.birthDate || basicInfoData?.dateOfBirthFormatted || "";
        quoteData.email = basicInfoData?.email || "";
        quoteData.phoneNumber = basicInfoData?.contactNumber || "";
      }

      console.log("Calling AMAN Quote API with data:", quoteData);

      const quoteResult = await getAmanQuote(quoteData, {
        token: loginTokenInfo?.Token || null,
      });

      if (quoteResult.success) {
        dispatch(setAmanQuoteResponse(quoteResult));
        handleNextSubstep();
      } else {
        dispatch(setAmanQuoteError(quoteResult.error));
        toast.error(
          translate(
            quoteResult.error || "Failed to get quote. Please try again.",
            "فشل في الحصول على عرض السعر. يرجى المحاولة مرة أخرى.",
          ),
        );
      }
    } catch (error) {
      console.error("Quote API error:", error);
      dispatch(setAmanQuoteError(error.message));
      toast.error(
        translate(
          "An error occurred. Please try again.",
          "حدث خطأ. يرجى المحاولة مرة أخرى.",
        ),
      );
    }
  };

  const isContinueDisabled = () => {
    return (
      quoteLoading ||
      !formData.fullName ||
      !formData.passportNumber ||
      !formData.cprNumber ||
      !formData.nationality ||
      !formData.placeOfBirth ||
      !formData.dateOfBirth.day ||
      !formData.dateOfBirth.month ||
      !formData.dateOfBirth.year ||
      !formData.gender ||
      !formData.occupation ||
      !formData.passportUploaded ||
      formData.passportProcessing
    );
  };

  const planData = amanFormData?.AMAN_PLAN_SELECTION;
  const basePremium = planData?.price || (planData?.planId === "aman_plus" ? 94 : 84);
  const selectedPremium = basePremium * 1.1;

  return (
    <div className="w-full">
      <AmanSummaryModal
        show={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
      />
      <AmanJourneyLayout
        is_hide_back_btn={false}
        customBackHandler={handlePrevSubstep}
        onClose={onClose}
      >
        <BottomStickyBtnLayout
          btn_onClick={handleContinue}
          btn_disabled={isContinueDisabled()}
          btn_label={translate("Continue", "استمرار")}
          isLoading={quoteLoading}
          show_summary={!!planData}
          price={selectedPremium}
          onShowSummary={() => setShowSummaryModal(true)}
        >
          <div className="w-full mx-auto max767:px-5 ipad:px-4">
            <div className="w-[500px] max767:w-full ipad:w-[500px] mx-auto">
              <TitleDescription
                title={translate("About Your Helper", "عن العاملة المنزلية")}
                description={translate(
                  "Please enter the required information about your helper.",
                  "يرجى إدخال المعلومات المطلوبة عن العاملة المنزلية.",
                )}
              />

              <div className="flex flex-col gap-6 pb-32">
                <div className="passport-upload-section">
                  <h3 className="text-[#48535B] text-base mb-1">
                    {translate(
                      "Upload Domestic Helper Passport",
                      "تحميل جواز سفر العاملة المنزلية",
                    )}
                  </h3>

                  <FileUpload
                    name="passport_helper"
                    toggleFileUpload={handlePassportUpload}
                    uploadedFile={passportFile}
                    key={passportFile?.fileName || "no-passport-file"}
                  />

                  {errors.passport && (
                    <p className="field-err text-red-500 text-sm mt-1 text-left rtl:text-right">
                      {errors.passport}
                    </p>
                  )}

                  {formData.passportProcessing && (
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center space-x-2 rtl:space-x-reverse">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <p className="text-sm text-blue-700">
                          {translate(
                            "Processing passport data...",
                            "جاري معالجة بيانات جواز السفر...",
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <Input
                  label={translate("Passport Number", "رقم جواز السفر")}
                  name="passportNumber"
                  value={formData.passportNumber}
                  handleChange={(e) =>
                    handleInputChange("passportNumber", e.target.value)
                  }
                  error={errors.passportNumber}
                  placeHolder={translate(
                    "Enter passport number",
                    "أدخل رقم جواز السفر",
                  )}
                />

                <Input
                  label={translate("CPR Number", "الرقم السكاني")}
                  name="cprNumber"
                  value={formData.cprNumber}
                  handleChange={(e) =>
                    handleInputChange("cprNumber", e.target.value)
                  }
                  error={errors.cprNumber}
                  placeHolder={translate(
                    "Enter CPR number",
                    "أدخل الرقم السكاني",
                  )}
                />

                <Input
                  label={translate(
                    "Full Name (as it appears in the passport)",
                    "الاسم الكامل (كما يظهر في جواز السفر)",
                  )}
                  name="fullName"
                  value={formData.fullName}
                  handleChange={(e) =>
                    handleInputChange("fullName", e.target.value)
                  }
                  error={errors.fullName}
                  placeHolder={translate(
                    "Enter full name",
                    "أدخل الاسم الكامل",
                  )}
                />

                <SelectInput
                  label={translate("Nationality", "الجنسية")}
                  name="nationality"
                  formData={formData}
                  handleChange={(e) =>
                    handleInputChange("nationality", e.target.value)
                  }
                  options_data={nationalityOptions}
                  default_placeholder={translate(
                    "Select nationality",
                    "اختر الجنسية",
                  )}
                  error={errors.nationality}
                  disabled={nationalitiesLoading}
                />

                <SelectInput
                  label={translate("Place of Birth", "مكان الميلاد")}
                  name="placeOfBirth"
                  formData={formData}
                  handleChange={(e) =>
                    handleInputChange("placeOfBirth", e.target.value)
                  }
                  options_data={countryOptions}
                  default_placeholder={translate(
                    "Select place of birth",
                    "اختر مكان الميلاد",
                  )}
                  error={errors.placeOfBirth}
                  disabled={countriesLoading}
                />

                <div className="form-field">
                  <label className="block text-[#48535b] text-base mb-2">
                    {translate("Date of Birth", "تاريخ الميلاد")}
                  </label>
                  <div className="flex gap-4">
                    <SelectInput
                      name="day"
                      formData={formData.dateOfBirth}
                      handleChange={(e) =>
                        handleDateChange("day", e.target.value)
                      }
                      options_data={daysOptions}
                      default_placeholder={translate("Day", "اليوم")}
                      className="flex-1"
                      maxMenuHeight={185}
                    />
                    <SelectInput
                      name="month"
                      formData={formData.dateOfBirth}
                      handleChange={(e) =>
                        handleDateChange("month", e.target.value)
                      }
                      options_data={monthsOptions}
                      default_placeholder={translate("Month", "الشهر")}
                      className="flex-1"
                      maxMenuHeight={185}
                    />
                    <SelectInput
                      name="year"
                      formData={formData.dateOfBirth}
                      handleChange={(e) =>
                        handleDateChange("year", e.target.value)
                      }
                      options_data={yearsOptions}
                      default_placeholder={translate("Year", "السنة")}
                      className="flex-1"
                      maxMenuHeight={185}
                    />
                  </div>
                  {errors.dateOfBirth && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.dateOfBirth}
                    </p>
                  )}
                </div>

                <FormTabs
                  label={translate("Gender", "الجنس")}
                  tabs={genderTabs}
                  activeTab={formData.gender}
                  onTabClick={handleGenderChange}
                  error={errors.gender}
                />

                <Input
                  label={translate(
                    "Domestic Application Number (if applicable)",
                    "رقم طلب العمالة المنزلية (إن وجد)",
                  )}
                  name="domesticApplicationNumber"
                  value={formData.domesticApplicationNumber}
                  handleChange={(e) =>
                    handleInputChange(
                      "domesticApplicationNumber",
                      e.target.value,
                    )
                  }
                  error={errors.domesticApplicationNumber}
                  placeHolder={translate(
                    "Enter application number",
                    "أدخل رقم الطلب",
                  )}
                />

                <SelectInput
                  label={translate("Occupation", "المهنة")}
                  name="occupation"
                  formData={formData}
                  handleChange={(e) =>
                    handleInputChange("occupation", e.target.value)
                  }
                  options_data={occupationOptions}
                  default_placeholder={translate(
                    "Select occupation",
                    "اختر المهنة",
                  )}
                  error={errors.occupation}
                />
              </div>
            </div>
          </div>
        </BottomStickyBtnLayout>
      </AmanJourneyLayout>
    </div>
  );
};

export default DomesticHelperDetailsPage;
